import { PlusOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Form,
  Input,
  message,
  Modal,
  Space,
  Typography,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { useState } from "react";
import { useUsers } from "../../../../hooks/useUser";
import { useAuth } from "../../../../hooks/useAuth";
import { AddEditUserForm } from "../components/AddEditUserForm";
import { UserFilter } from "../components/UserFilter";
import { UserList } from "../components/UserList";

const { Title } = Typography;

export function UserPage() {
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form] = useForm();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [passwordForm] = useForm();
  const { data: user } = useAuth();
  const [filter, setFilter] = useState({ page: 1, limit: 10 });
  const { data, total, loading, create, update, remove, adminChangePassword } =
    useUsers(filter);

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
    form.resetFields();
  };

  const handleAdd = () => {
    setSelectedItem(null);
    form.resetFields();
    setOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    form.setFieldsValue(item);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await remove.mutateAsync(id);
      message.success("Xóa dữ liệu thành công.");
    } catch (error) {
      console.error(error);
      message.error(`${error || "Lỗi hệ thống."}`);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (selectedItem) {
        await update.mutateAsync({ ...values, _id: selectedItem._id });
      } else {
        await create.mutateAsync(values);
      }
      message.success("Tạo dữ liệu thành công");
      setOpen(false);
      form.resetFields();
    } catch (error) {
      console.error(error);
      message.error(`${error || "Lỗi hệ thống."}`);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleChangePasswordClick = (record) => {
    setSelectedUserForPassword(record);
    passwordForm.resetFields();
    setPasswordModalOpen(true);
  };

  const handlePasswordSubmit = async (values) => {
    try {
      await adminChangePassword.mutateAsync({
        id: selectedUserForPassword._id,
        newPassword: values.newPassword,
      });
      message.success("Đổi mật khẩu thành công!");
      setPasswordModalOpen(false);
      setSelectedUserForPassword(null);
      passwordForm.resetFields();
    } catch (error) {
      message.error(`${error || "Đổi mật khẩu thất bại."}`);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Breadcrumb
            items={[{ title: "Bảng điều khiển" }, { title: "Người dùng" }]}
          />
          <Title level={3} style={{ margin: 0 }}>
            Quản lý người dùng
          </Title>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Thêm người dùng
        </Button>
      </div>

      <p style={{ margin: "8px 0 0 0", color: "#666" }}>
        Tổng số: {total} người
      </p>

      <div style={{ padding: "24px 0" }}>
        <UserFilter filter={filter} onFilterChange={handleFilterChange} />
      </div>

      <div>
        <UserList
          data={data}
          total={total}
          loading={loading}
          filter={filter}
          onFilterChange={handleFilterChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
          user={user}
          onChangePassword={handleChangePasswordClick}
        />
      </div>

      <Modal
        title={selectedItem ? "Cập nhật người dùng" : "Thêm người dùng"}
        open={open}
        onCancel={handleClose}
        footer={null}
      >
        <AddEditUserForm
          form={form}
          loading={create.isPending || update.isPending}
          onSubmit={handleSubmit}
          onClose={handleClose}
        />
      </Modal>

      <Modal
        title="Đổi mật khẩu"
        open={passwordModalOpen}
        onCancel={() => {
          setPasswordModalOpen(false);
          setSelectedUserForPassword(null);
          passwordForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="Mật khẩu mới"
            name="newPassword"
            rules={[
              { required: true, message: "Vui lòng nhập mật khẩu mới!" },
              { min: 6, message: "Mật khẩu phải có ít nhất 6 ký tự!" },
            ]}
          >
            <Input.Password size="large" placeholder="Nhập mật khẩu mới" />
          </Form.Item>

          <Form.Item
            label="Xác nhận mật khẩu"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Vui lòng xác nhận mật khẩu!" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Mật khẩu xác nhận không khớp!"),
                  );
                },
              }),
            ]}
          >
            <Input.Password size="large" placeholder="Nhập lại mật khẩu mới" />
          </Form.Item>

          <Form.Item>
            <Space style={{ justifyContent: "flex-end", width: "100%" }}>
              <Button
                size="large"
                variant="outlined"
                loading={adminChangePassword.isPending}
                onClick={() => {
                  setPasswordModalOpen(false);
                  setSelectedUserForPassword(null);
                  passwordForm.resetFields();
                }}
              >
                Hủy
              </Button>
              <Button
                size="large"
                type="primary"
                htmlType="submit"
                loading={adminChangePassword.isPending}
              >
                Xác nhận đổi
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
